import React from "react";
import { CalendarDays } from "lucide-react";
import { useGetCalendarDataQuery } from "../../store/apis/attendance.api";
import { getStatusCategory } from "../../constants/attendance";
import { CARD_CLASS, WEEKDAY_SHORT } from "../../constants/dashboard";

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatCheckIn(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const ThisWeekStrip: React.FC = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  const { data: calendarResponse, isFetching } = useGetCalendarDataQuery({
    month,
    year,
  });

  const days = calendarResponse?.data?.days || [];
  const todayStr = toDateStr(now);

  const dayIdx = now.getDay();
  const diffToMonday = dayIdx === 0 ? -6 : 1 - dayIdx;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMonday);
  const weekDays = WEEKDAY_SHORT.map((label, i) => {
    const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    return { label, date, dateStr: toDateStr(date) };
  });

  const friday = weekDays[weekDays.length - 1].date;

  return (
    <div className={`${CARD_CLASS} p-4 sm:p-5`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="m-0 text-heading-sm font-bold text-text-primary flex items-center gap-2 tracking-tight">
          <CalendarDays className="w-[18px] h-[18px] text-primary-600" />
          This Week
        </h3>
        <span className="text-caption font-semibold text-text-tertiary">
          {monday.getDate()}–{friday.getDate()} {MONTH_SHORT[friday.getMonth()]}{" "}
          {friday.getFullYear()}
        </span>
      </div>

      <div className="flex sm:grid sm:grid-cols-5 gap-3 overflow-x-auto p-1 -m-1 scrollbar-thin">
        {weekDays.map(({ label, date, dateStr }) => {
          const dayData = days.find((d) => d.date === dateStr);
          const isToday = dateStr === todayStr;
          const isFuture = dateStr > todayStr;

          let chipLabel = "—";
          let sub = "";
          let color = "var(--color-text-tertiary)";
          let bg = "var(--color-layout-hover)";

          if (!isFuture && dayData?.isHoliday) {
            chipLabel = dayData.holidayName?.trim() || "Week off";
            sub = "Holiday";
            color = "var(--color-warning-dark)";
            bg = "var(--color-warning-light)";
          } else if (!isFuture && dayData?.status) {
            const cat = getStatusCategory(dayData.status);
            if (cat === "present") {
              chipLabel = "Office";
              sub = isToday && formatCheckIn(dayData.checkInTime)
                ? `In: ${formatCheckIn(dayData.checkInTime)}`
                : formatCheckIn(dayData.checkInTime) || "Full day";
              color = "var(--color-success-dark)";
              bg = "var(--color-success-light)";
            } else if (cat === "absent") {
              chipLabel = "Absent";
              sub = "—";
              color = "var(--color-error-dark)";
              bg = "var(--color-error-light)";
            } else if (cat === "leave") {
              chipLabel = "Leave";
              sub = "On Leave";
              color = "var(--color-primary-700)";
              bg = "var(--color-primary-50)";
            } else {
              chipLabel = "—";
              sub = "";
            }
          }

          return (
            <div
              key={dateStr}
              className={`flex-1 min-w-[85px] sm:min-w-0 rounded-card px-2.5 py-3.5 text-center border transition-all duration-150 flex flex-col justify-between ${
                isToday
                  ? "border-primary-600 bg-primary-50/20 ring-1 ring-primary-600"
                  : "border-border-secondary bg-surface hover:border-border-primary"
              }`}
            >
              <div className={`text-[12px] font-bold ${isToday ? "text-primary-700" : "text-text-secondary"}`}>
                {label} {date.getDate()}
              </div>
              <span
                className="inline-block mt-2 mb-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full truncate max-w-full mx-auto"
                style={{ color, background: bg }}
                title={chipLabel}
              >
                {chipLabel}
              </span>
              <div className="text-body-xs text-text-tertiary truncate" title={sub}>
                {sub || "—"}
              </div>
            </div>
          );
        })}
      </div>

      {isFetching && (
        <div className="text-caption text-text-tertiary mt-3">
          Updating this week's schedule…
        </div>
      )}
    </div>
  );
};

export default ThisWeekStrip;
